import { apiClient } from '@/lib/apiClient';
import axios from 'axios';

export interface EnumValueDto {
  code: string;
  displayName: string;
  ordinal: number;
  isTerminal: boolean;
  description?: string;
}

export interface EnumTypeMetadataDto {
  showNumericCode: boolean;
}

/**
 * Cache for enum values (in-memory + localStorage)
 */
class EnumCache {
  private memoryCache: Map<string, EnumValueDto[]> = new Map();
  private readonly CACHE_KEY_PREFIX = 'enum:';
  private readonly CACHE_TTL = 3600000; // 1 hour in milliseconds

  /**
   * Get enum values from cache
   */
  get(enumType: string): EnumValueDto[] | null {
    // Check memory cache first
    if (this.memoryCache.has(enumType)) {
      return this.memoryCache.get(enumType)!;
    }

    // Check localStorage
    try {
      const cached = localStorage.getItem(`${this.CACHE_KEY_PREFIX}${enumType}`);
      if (cached) {
        const { values, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        
        if (age < this.CACHE_TTL) {
          // Cache is still valid
          this.memoryCache.set(enumType, values);
          return values;
        } else {
          // Cache expired, remove it
          localStorage.removeItem(`${this.CACHE_KEY_PREFIX}${enumType}`);
        }
      }
    } catch (error) {
      console.warn(`Failed to read enum cache for ${enumType}:`, error);
    }

    return null;
  }

  /**
   * Set enum values in cache
   */
  set(enumType: string, values: EnumValueDto[]): void {
    // Update memory cache
    this.memoryCache.set(enumType, values);

    // Update localStorage
    try {
      localStorage.setItem(
        `${this.CACHE_KEY_PREFIX}${enumType}`,
        JSON.stringify({
          values,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      console.warn(`Failed to write enum cache for ${enumType}:`, error);
    }
  }

  /**
   * Invalidate cache for an enum type
   */
  invalidate(enumType: string): void {
    this.memoryCache.delete(enumType);
    localStorage.removeItem(`${this.CACHE_KEY_PREFIX}${enumType}`);
  }

  /**
   * Clear all enum caches
   */
  clear(): void {
    this.memoryCache.clear();
    Object.keys(localStorage)
      .filter(key => key.startsWith(this.CACHE_KEY_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  }
}

const cache = new EnumCache();

/**
 * Cache for enum type metadata (showNumericCode etc.)
 */
let metadataCache: Record<string, EnumTypeMetadataDto> | null = null;
let metadataPromise: Promise<Record<string, EnumTypeMetadataDto>> | null = null;

/**
 * Enum service for fetching dynamic enum values
 */
class EnumService {
  /**
   * Get enum values for a single enum type
   */
  async getEnumValues(enumType: string): Promise<EnumValueDto[]> {
    // Check cache first
    const cached = cache.get(enumType);
    if (cached) {
      // Ensure cached value is an array
      if (!Array.isArray(cached)) {
        console.warn(`Cached value for ${enumType} is not an array, clearing cache:`, cached);
        cache.invalidate(enumType);
      } else {
        return cached;
      }
    }

    // Fetch from API using apiClient (includes auth interceptors)
    try {
      const response = await apiClient.get<EnumValueDto[]>(
        `/api/enums/${enumType}`,
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Check response status
      if (response.status !== 200) {
        throw new Error(`API returned status ${response.status} for enum ${enumType}`);
      }
      
      // Check content type - if HTML, it's likely an error page
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('text/html')) {
        console.error(`API returned HTML instead of JSON for enum ${enumType}. Response:`, response.data);
        throw new Error(`API endpoint returned HTML page. This usually means the endpoint '/api/enums/${enumType}' doesn't exist, requires authentication, or there's a server error.`);
      }
      
      const data = response.data;

      // Handle HTML responses (error pages, redirects, etc.) - double check even if content-type says JSON
      if (typeof data === 'string') {
        const dataStr = data as string;
        if (dataStr.trim().startsWith('<!DOCTYPE') || dataStr.trim().startsWith('<!doctype') || dataStr.includes('<html')) {
          console.error(`API returned HTML page instead of JSON for enum ${enumType}. This usually means the endpoint doesn't exist or there's an authentication issue.`);
          throw new Error(`API endpoint not found or authentication required. Please check if '/api/enums/${enumType}' exists and you have permission to access it.`);
        }
      }

      // Handle string responses (error messages from API)
      if (typeof data === 'string') {
        console.error(`API returned string instead of array for enum ${enumType}:`, data);
        throw new Error(`API error: ${data}`);
      }

      // Handle ABP error response format
      if (data && typeof data === 'object' && 'error' in data) {
        const errorMessage = (data as any).error?.message || (data as any).message || 'Unknown error';
        console.error(`API returned error object for enum ${enumType}:`, data);
        throw new Error(`API error: ${errorMessage}`);
      }

      // Ensure response is an array
      if (!Array.isArray(data)) {
        console.error(`API returned non-array for enum ${enumType}:`, {
          data,
          type: typeof data,
          isArray: Array.isArray(data),
          response: response
        });
        throw new Error(`Invalid response format: expected array, got ${typeof data}`);
      }

      // Cache the result
      cache.set(enumType, data);

      return data;
    } catch (error: any) {
      // If axios error, extract the actual error message
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data;
        
        // Handle string error responses
        if (typeof errorData === 'string') {
          console.error(`API returned string error for enum ${enumType}:`, errorData);
          throw new Error(errorData);
        }
        
        // Handle ABP error format
        if (errorData?.error?.message) {
          console.error(`API returned ABP error for enum ${enumType}:`, errorData);
          throw new Error(errorData.error.message);
        }
        
        // Handle standard error message
        if (errorData?.message) {
          console.error(`API returned error message for enum ${enumType}:`, errorData);
          throw new Error(errorData.message);
        }
        
        // Handle HTTP status errors with specific messages
        if (error.response?.status === 404) {
          throw new Error(`Enum type '${enumType}' not found. Please ensure the enum is registered in the system.`);
        }
        
        if (error.response?.status === 401 || error.response?.status === 403) {
          throw new Error(`Permission denied: Unable to access enum '${enumType}'. Please check your permissions.`);
        }
        
        if (error.response?.status === 500) {
          // Try to extract more specific error message from 500 response
          const errorMsg = errorData?.error?.message || errorData?.message || 'Internal server error';
          throw new Error(`Server error: ${errorMsg}. Please check the server logs or contact support.`);
        }
      }
      
      console.error(`Failed to fetch enum values for ${enumType}:`, {
        error,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      
      // Use the error's message if it's already a meaningful Error object
      if (error instanceof Error && error.message && !error.message.includes('Request failed')) {
        throw error;
      }
      
      throw new Error(
        `Failed to fetch enum values for ${enumType}: ${error.message || 'Unknown error'}`
      );
    }
  }

  /**
   * Get multiple enum types in a single call (bulk loading)
   */
  async getMultipleEnumValues(enumTypes: string[]): Promise<Record<string, EnumValueDto[]>> {
    // Check cache for all types
    const uncachedTypes: string[] = [];
    const result: Record<string, EnumValueDto[]> = {};

    for (const enumType of enumTypes) {
      const cached = cache.get(enumType);
      if (cached) {
        result[enumType] = cached;
      } else {
        uncachedTypes.push(enumType);
      }
    }

    // Fetch uncached types in bulk
    if (uncachedTypes.length > 0) {
      try {
        const response = await apiClient.get<Record<string, EnumValueDto[]>>(
          `/api/enums`,
          {
            params: {
              types: uncachedTypes.join(','),
            },
          }
        );

        // Cache each enum type separately
        for (const [enumType, values] of Object.entries(response.data)) {
          cache.set(enumType, values);
          result[enumType] = values;
        }
      } catch (error: any) {
        console.error('Failed to fetch multiple enum values:', error);
        throw new Error(
          error.response?.data?.message || 'Failed to fetch enum values'
        );
      }
    }

    return result;
  }

  /**
   * Get a specific enum value by code
   */
  async getEnumValue(enumType: string, code: string): Promise<EnumValueDto | null> {
    const values = await this.getEnumValues(enumType);
    return values.find(v => v.code === code) || null;
  }

  /**
   * Get all available enum type names (static + dynamic)
   */
  async getEnumTypes(): Promise<string[]> {
    const response = await apiClient.get<string[]>('/api/enums/types');
    return response.data;
  }

  /**
   * Get display metadata for all enum types (cached after first call)
   */
  async getMetadata(): Promise<Record<string, EnumTypeMetadataDto>> {
    if (metadataCache) {
      return metadataCache;
    }

    // Deduplicate concurrent requests
    if (!metadataPromise) {
      metadataPromise = apiClient
        .get<Record<string, EnumTypeMetadataDto>>('/api/enums/metadata')
        .then((response) => {
          metadataCache = response.data;
          metadataPromise = null;
          return metadataCache;
        })
        .catch((error) => {
          metadataPromise = null;
          console.warn('Failed to fetch enum metadata:', error);
          return {};
        });
    }

    return metadataPromise;
  }

  /**
   * Get metadata for a specific enum type
   */
  async getEnumTypeMetadata(enumType: string): Promise<EnumTypeMetadataDto | null> {
    const metadata = await this.getMetadata();
    return metadata[enumType] || null;
  }

  /**
   * Invalidate cache for an enum type
   */
  invalidateCache(enumType: string): void {
    cache.invalidate(enumType);
  }

  /**
   * Clear all enum caches
   */
  clearCache(): void {
    cache.clear();
    metadataCache = null;
    metadataPromise = null;
  }
}

export const enumService = new EnumService();

